import { useState } from "react";
import { Button, ButtonVariant, Text, TextField } from "@merge-api/merge-javascript-shared";
import { Plus, X } from "lucide-react";

interface Props {
  metadata: Record<string, unknown>;
  onChange: (metadata: Record<string, unknown>) => void;
}

// Coerce string input into bool/number where it looks like one, matching how
// step metadata is typically stored (e.g. reset_timestamps: true).
const coerce = (value: string): unknown => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (/^-?\d+$/.test(value)) return Number(value);
  if (/^-?\d*\.\d+$/.test(value)) return Number(value);
  return value;
};

// Simple key/value editor for a step's metadata object.
const MetadataEditor = ({ metadata, onChange }: Props) => {
  const [newKey, setNewKey] = useState("");
  const entries = Object.entries(metadata || {});

  const setValue = (key: string, value: string) => onChange({ ...metadata, [key]: coerce(value) });
  const removeKey = (key: string) => {
    const next = { ...metadata };
    delete next[key];
    onChange(next);
  };
  const addKey = () => {
    const key = newKey.trim();
    if (!key) return;
    onChange({ ...metadata, [key]: "" });
    setNewKey("");
  };

  return (
    <div className="flex flex-col gap-2">
      {entries.length === 0 && (
        <Text variant="sm" className="italic text-gray-60">
          No metadata.
        </Text>
      )}
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <Text variant="sm" className="w-40 shrink-0 truncate font-mono text-gray-70">
            {key}
          </Text>
          <div className="flex-1">
            <TextField
              className="bg-white"
              value={String(value)}
              onChange={(e) => setValue(key, e.target.value)}
            />
          </div>
          <button
            onClick={() => removeKey(key)}
            aria-label={`Remove ${key}`}
            className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <TextField
            className="bg-white"
            value={newKey}
            placeholder="new key (e.g. reset_timestamps)"
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addKey();
            }}
          />
        </div>
        <Button
          size="sm"
          variant={ButtonVariant.SecondaryGray}
          leftIcon={<Plus size={14} />}
          onClick={addKey}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

export default MetadataEditor;
